/**
 * SIL-1346 - the ONE place API error codes become user-facing copy for the
 * Exchange buyer surfaces. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md §6, and the
 * error table in §6 of
 * docs/superpowers/specs/2026-09-28-exchange-buyer-verification-and-gating-design.md.
 * Wording follows docs/exchange/design-handoff/README.md §5 where the handoff
 * spells a message out verbatim (the OTP mismatch sentence in particular).
 *
 * Every screen in slice 3 (marketplace, listing detail, node drawer, buyer
 * verification modal, My Activity) imports `exchangeErrorMessage` rather than
 * translating codes itself. Two reasons this is a module and not a switch in
 * each component:
 *
 *  1. The same code surfaces in several places - `BUYER_VERIFICATION_REQUIRED`
 *     comes back from offer create, bid create, buy and the passport-summary
 *     drawer fetch - and four copies of a sentence drift.
 *  2. The codes are a server contract (`apps/api/src/routes/client/
 *     buyer-verification.ts` and `middleware/clientAuth.ts`'s
 *     `buyerVerificationRequiredError`). One table can be diffed against that
 *     contract; scattered string literals cannot.
 *
 * The error objects read here are the ones `lib/api.ts`'s `throwIfNotOk`
 * throws: an `Error` with `status` and the parsed `body` attached, where the
 * body is `{ error: { code, message, ...extra } }` for every route in this
 * feature.
 */

import type { SmsSendTwilioErrorCode } from '@siliconx/shared';
import {
  BUYER_GATE_CALLOUT,
  BUYER_VERIFICATION_MEMBER_NOTE,
  BUYER_VERIFICATION_PHONE_UNDELIVERABLE,
  BUYER_VERIFICATION_SMS_PROVIDER_UNAVAILABLE,
} from '@siliconx/shared/ui/buyer-listing/copy/buyerVerification';

/** The 403 code every gated buyer action returns for an unverified org. */
export const BUYER_VERIFICATION_REQUIRED = 'BUYER_VERIFICATION_REQUIRED';

/** Shape `throwIfNotOk` attaches - every field optional, nothing is trusted. */
interface ApiErrorBody {
  error?: { code?: unknown; message?: unknown } & Record<string, unknown>;
}

function errorBody(e: unknown): ApiErrorBody['error'] | null {
  if (typeof e !== 'object' || e === null) return null;
  const body = (e as { body?: unknown }).body;
  if (typeof body !== 'object' || body === null) return null;
  const error = (body as ApiErrorBody).error;
  // A plain-string `error` body (the majority shape elsewhere in the API -
  // see throwIfNotOk's own comment) carries no code; treat it as codeless
  // rather than reading `.code` off a string.
  if (typeof error !== 'object' || error === null) return null;
  return error as ApiErrorBody['error'];
}

/** HTTP status `throwIfNotOk` attached, or null when this is not an API error. */
export function apiErrorStatus(e: unknown): number | null {
  const status = typeof e === 'object' && e !== null ? (e as { status?: unknown }).status : undefined;
  return typeof status === 'number' ? status : null;
}

/** The `error.code` the API returned, or null when there is none. */
export function apiErrorCode(e: unknown): string | null {
  const code = errorBody(e)?.code;
  return typeof code === 'string' ? code : null;
}

/** An extra field the API sent alongside the code (`attemptsLeft`, `retryAfter`, `missing`). */
export function apiErrorDetail(e: unknown, key: string): unknown {
  return errorBody(e)?.[key];
}

/**
 * True only for the gate's own 403. Both the status and the code are checked:
 * a future route that returns this code on a non-403 is not an action gate,
 * and `useVerificationGate` must not open the verification modal for it.
 */
export function isBuyerVerificationRequired(e: unknown): boolean {
  return apiErrorStatus(e) === 403 && apiErrorCode(e) === BUYER_VERIFICATION_REQUIRED;
}

/**
 * `VERIFICATION_INCOMPLETE` carries the field names the definer function found
 * missing. The verification modal uses them to jump to the first step that
 * needs attention (spec §6) - returns `[]` for any other error.
 */
export function verificationIncompleteMissing(e: unknown): string[] {
  const missing = apiErrorDetail(e, 'missing');
  return Array.isArray(missing) ? missing.filter((m): m is string => typeof m === 'string') : [];
}

/**
 * Code → user-facing sentence. Keys are the literal codes
 * `routes/client/buyer-verification.ts` emits (the lower-case two are its
 * `writeGate` codes, kept exactly as the server spells them - a "tidied"
 * upper-case key here would silently never match).
 *
 * Codes that carry a number (`OTP_MISMATCH`'s `attemptsLeft`,
 * `RATE_LIMITED`'s `retryAfter`) have a base sentence here and a suffix
 * appended by `exchangeErrorMessage`, so the table stays greppable, plain
 * strings.
 */
/**
 * `& Record<SmsSendTwilioErrorCode, string>` is the end-to-end type link
 * (SIL-1575): every code `classifyTwilioVerifyError` (apps/api/.../
 * twilio-verify.ts) can produce is a required key of this object literal, so
 * a future code added to the shared union with no matching entry here is a
 * compile error in this file, not a silent `EXCHANGE_GENERIC_ERROR` toast.
 */
export const EXCHANGE_ERROR_MESSAGES: Record<string, string> & Record<SmsSendTwilioErrorCode, string> = {
  [BUYER_VERIFICATION_REQUIRED]: BUYER_GATE_CALLOUT,
  OTP_MISMATCH: 'That code does not match. Check the latest SMS and try again.',
  OTP_EXPIRED: 'That code expired. Request a new code.',
  OTP_LOCKED: 'Too many wrong codes. Contact support to continue verification.',
  RATE_LIMITED: 'Wait before you request another code.',
  SMS_SEND_FAILED: 'We could not send the code. Try again in a moment.',
  // SIL-1506 (Twilio Verify): non-US/CA or toll-free numbers, and a check outage.
  PHONE_NOT_SUPPORTED: 'Enter a US or Canadian mobile number.',
  // SIL-1575: Twilio itself rejected the number as permanently undeliverable
  // (invalid / non-mobile / region unsupported) - never leaked, just this code.
  PHONE_UNDELIVERABLE: BUYER_VERIFICATION_PHONE_UNDELIVERABLE,
  // SIL-1575: our Twilio account/configuration (e.g. trial-account 21608), not the user's number.
  SMS_PROVIDER_UNAVAILABLE: BUYER_VERIFICATION_SMS_PROVIDER_UNAVAILABLE,
  SMS_VERIFY_UNAVAILABLE: 'We could not check the code. Try again in a moment.',
  VERIFICATION_INCOMPLETE: 'Some required details are still missing. Check the highlighted steps.',
  DOCUMENT_MISSING: 'Attach the certificate of incorporation or good standing before submitting.',
  INVALID_TRANSITION: 'This verification is already under review. Reload the page to see its current state.',
  business_org_required: 'Buyer verification is for business accounts.',
  org_admin_required: BUYER_VERIFICATION_MEMBER_NOTE,
  // SIL-1427: PURCHASE_PENDING has its own dedicated handling (refetch +
  // "Purchase pending" state, not a toast) at the Buy now call site — this
  // entry is the fallback if the code ever surfaces through a generic path.
  PURCHASE_PENDING: 'Purchase pending — another buyer already requested this listing.',
  LISTING_NOT_AVAILABLE: 'This listing is no longer available to buy now.',
  // SIL-1427: backstop only — the UI never offers Counter on a buy_now row.
  BUY_NOW_NOT_COUNTERABLE: 'A Buy now request cannot be countered — confirm availability or decline it instead.',
};

/** Fallback for an error with neither a mapped code nor a server message. */
export const EXCHANGE_GENERIC_ERROR = 'Something went wrong. Try again.';

/**
 * Codes the server sends with a MORE specific user-facing message than one
 * sentence in the table above can be. `DOCUMENT_MISSING` is returned for three
 * distinct causes - no object at the staging key, over 10 MB, and an
 * unsupported content type - each with its own already-user-facing message, so
 * collapsing them to the table's generic sentence would throw away the only
 * information that tells the user what to do differently. For these codes the
 * server message wins and the table entry is the fallback.
 */
const SERVER_MESSAGE_PREFERRED = new Set(['DOCUMENT_MISSING']);

function countSuffix(value: unknown, singular: string, plural: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return '';
  return ` ${value} ${value === 1 ? singular : plural}`;
}

/**
 * The single translation every screen calls. Precedence, in order:
 *
 *  1. A code in `EXCHANGE_ERROR_MESSAGES` - our copy wins over the server's,
 *     so the wording is reviewable in this file.
 *  2. Otherwise the server's own `error.message`, which the client routes
 *     already write user-facing (a 429 from an unmapped route, say).
 *  3. Otherwise the thrown `Error`'s message, then `fallback`.
 */
export function exchangeErrorMessage(e: unknown, fallback: string = EXCHANGE_GENERIC_ERROR): string {
  const code = apiErrorCode(e);
  const mapped = code ? EXCHANGE_ERROR_MESSAGES[code] : undefined;
  if (mapped && code !== null && SERVER_MESSAGE_PREFERRED.has(code)) {
    const specific = errorBody(e)?.message;
    return typeof specific === 'string' && specific.trim() !== '' ? specific : mapped;
  }
  if (mapped) {
    if (code === 'OTP_MISMATCH') {
      return mapped + countSuffix(apiErrorDetail(e, 'attemptsLeft'), 'attempt left.', 'attempts left.');
    }
    if (code === 'RATE_LIMITED') {
      const retryAfter = apiErrorDetail(e, 'retryAfter');
      return typeof retryAfter === 'number' && Number.isFinite(retryAfter) && retryAfter > 0
        ? `Wait ${Math.ceil(retryAfter)}s before you request another code.`
        : mapped;
    }
    return mapped;
  }

  const serverMessage = errorBody(e)?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim() !== '') return serverMessage;

  const thrown = typeof e === 'object' && e !== null ? (e as { message?: unknown }).message : undefined;
  if (typeof thrown === 'string' && thrown.trim() !== '') return thrown;

  return fallback;
}
